// viewTours — one short guided tour per main view (Projects, the Studio,
// Search's Materials and Sources tabs), and which views have already
// offered theirs. Pure, apart from the copy hook.
//
//  - `tourViewOf`: the view on screen that has a tour, or none (Settings, a
//    source or material gone into, the Studio with no project open);
//  - `ToursSeen`: the views whose tour was offered or taken, so the quiet
//    offer comes once per view. Kept per device (app/tourSeen.ts): there is
//    no server-side preference store yet, like the Projects view and the
//    pins. A view name from a newer build is dropped at the parse;
//  - `useViewTours`: each tour's steps in the author's words, every step
//    anchored to a real element (`TourTargetId`). The copy says only what
//    the code does today.

export type { ToursSeen, ToursSeenCommand, TourView, ViewTour };
export {
    foldToursSeen,
    NO_TOURS_SEEN,
    offerFor,
    parseToursSeen,
    serializeToursSeen,
    tourViewOf,
    useViewTours,
};

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ActiveTab } from "../Navigation/types.js";
import { tabOfFacet } from "../SearchPage/searchRows.js";
import type { Tour } from "./GuidedTour.js";

type TourView = "projects" | "studio" | "materials" | "sources";

const TOUR_VIEWS: readonly TourView[] = [
    "projects",
    "studio",
    "materials",
    "sources",
];

// The view with a tour on screen. `studioOnProject`: the Studio shows a
// project (not its empty view, nor the classic canvas), so its outline,
// page and panel are there to point at.
const tourViewOf = (
    active: ActiveTab,
    studioOnProject: boolean,
): TourView | null => {
    switch (active.type) {
        case "projects":
            return "projects";
        case "studio":
            return studioOnProject ? "studio" : null;
        case "search": {
            // An item gone into replaces the list: nothing to tour there.
            if (active.selectedId !== undefined) return null;
            return tabOfFacet(active.kind);
        }
        case "resource":
        case "conversation":
        case "settings":
        case "home":
        case "knowledge":
        case "quality":
            return null;
        default:
            return exhaustSwitchCase(active);
    }
};

// The view on screen's tour as the Help menu offers it (pen Q68): its name,
// whether it is New (never taken on this device; pen's New badge), and the
// intent that starts it.
type ViewTour = {
    readonly name: string;
    readonly isNew: boolean;
    readonly start: () => void;
};

// === Which views have offered their tour ====================================

type ToursSeen = ReadonlySet<TourView>;

// The offer showed (and went, or was closed), or the author took the tour
// from the menu: two intents, one outcome, the view is not offered again.
// Reset is the dev "reset onboarding".
type TourOffered = { readonly type: "tour_offered"; readonly view: TourView };
type TourStarted = { readonly type: "tour_started"; readonly view: TourView };
type ToursReset = { readonly type: "tours_reset" };
type ToursSeenCommand = TourOffered | TourStarted | ToursReset;

const NO_TOURS_SEEN: ToursSeen = new Set();

const foldToursSeen = (seen: ToursSeen, cmd: ToursSeenCommand): ToursSeen => {
    switch (cmd.type) {
        case "tour_offered":
        case "tour_started":
            return seen.has(cmd.view) ? seen : new Set([...seen, cmd.view]);
        case "tours_reset":
            return seen.size === 0 ? seen : NO_TOURS_SEEN;
        default:
            return exhaustSwitchCase(cmd);
    }
};

// A JSON list of view names; anything else reads as nothing seen.
const parseToursSeen = (raw: string | null): ToursSeen => {
    if (raw === null) return NO_TOURS_SEEN;
    let data: unknown;
    try {
        data = JSON.parse(raw);
    } catch {
        return NO_TOURS_SEEN;
    }
    if (!Array.isArray(data)) return NO_TOURS_SEEN;
    return new Set(TOUR_VIEWS.filter((view) => data.includes(view)));
};

const serializeToursSeen = (seen: ToursSeen): string =>
    JSON.stringify(TOUR_VIEWS.filter((view) => seen.has(view)));

// The view whose tour to offer now: the one on screen, the first time.
const offerFor = (view: TourView | null, seen: ToursSeen): TourView | null =>
    view !== null && !seen.has(view) ? view : null;

// === The tours ===============================================================

const useViewTours = (): Readonly<Record<TourView, Tour>> => {
    const { t } = useLingui();
    return {
        projects: {
            name: t`Projects`,
            steps: [
                {
                    target: "projects-toolbar",
                    placement: "bottom",
                    title: t`All your projects`,
                    body: t`Every project you are making is here. Search them, sort them, or show them as a table.`,
                },
                {
                    target: "new-project",
                    placement: "right",
                    title: t`New project`,
                    body: t`Say what you want to make and add files if you have them. Everything can be changed later.`,
                },
                {
                    target: "project-open",
                    placement: "bottom",
                    title: t`Open a project`,
                    body: t`Click a project to open it in the Studio, on the page where you left off.`,
                },
            ],
        },
        studio: {
            name: t`Studio`,
            steps: [
                {
                    target: "studio-outline",
                    placement: "right",
                    title: t`The outline`,
                    body: t`Info says what the project is for and who it is for. Learning objectives shows which parts cover each one. Below them are the parts.`,
                },
                {
                    target: "studio-page",
                    placement: "right",
                    title: t`Write on the page`,
                    body: t`Click a line to write. Type / on a new line to open the Create menu.`,
                },
                {
                    target: "studio-create",
                    placement: "right",
                    title: t`Ask here`,
                    body: t`Create opens the same menu. Pick Ask here and say what you want. The new part lands where you are.`,
                },
                {
                    target: "studio-panel",
                    placement: "left",
                    title: t`Chat and Needs you`,
                    body: t`Chat is where you talk with the assistant about this project. Needs you lists what waits for you before export.`,
                },
                {
                    target: "studio-export",
                    placement: "bottom",
                    title: t`Export`,
                    body: t`When the material is ready, Export packages it in the format you pick.`,
                },
            ],
        },
        materials: {
            name: t`Materials`,
            steps: [
                {
                    target: "search-field",
                    placement: "bottom",
                    title: t`Materials`,
                    body: t`What you've made, from all your projects. Type to find one by its title.`,
                },
                {
                    target: "search-filters",
                    placement: "bottom",
                    title: t`Filter`,
                    body: t`Show one kind only, like quizzes. Sort sits just before it.`,
                },
                {
                    target: "search-results",
                    placement: "bottom",
                    title: t`Open a material`,
                    body: t`Click a row to open it in its project. The … on a row renames, archives or deletes it.`,
                },
            ],
        },
        sources: {
            name: t`Sources`,
            steps: [
                {
                    target: "search-field",
                    placement: "bottom",
                    title: t`Sources`,
                    body: t`What you've brought in. Type to find one by its title or author.`,
                },
                {
                    target: "search-filters",
                    placement: "bottom",
                    title: t`Filter`,
                    body: t`Show one type of file only, like PDF. Sort sits just before it.`,
                },
                {
                    target: "search-results",
                    placement: "bottom",
                    title: t`Open a source`,
                    body: t`Click a row to read it. The … on a row renames, archives or deletes it.`,
                },
            ],
        },
    };
};
